import React from "react";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { selectStoreConfig } from "../redux/Slice/siteSlice";
import { money, discountPercent, PLACEHOLDER_IMG, shortText, sizedImage } from "../utils/formatters";
import ProductShare from "./ProductShare";
import { colorsOf, needsPick, qtyInCart, shownPrice, sizesOf } from "../utils/variants";

// Product tile used in lists and sliders: price, discount, add to cart / quantity
export default function ProductCard({ p, add, onSelect, cart = [], updateQty }) {
    const config = useSelector(selectStoreConfig);
    // A product sold in sizes or colours is added through the size picker, one line per pick, so it has no
    // quantity buttons here: only how many are in the bag
    const picks = needsPick(p);
    const quantity = picks ? 0 : cart.find((item) => item.id === p.id)?.qty || 0;
    const inBag = picks ? qtyInCart(cart, p) : 0;
    const inStock = p.inStock !== false;
    const shown = shownPrice(p); // "From" the cheapest size when sizes cost different amounts
    const discount = discountPercent(shown);
    const badge = !inStock ? "Out of stock" : discount ? `${discount}% off` : "";

    const changeQuantity = (event, nextQuantity) => {
        event.stopPropagation();
        if (typeof updateQty === "function") {
            updateQty(p.id, nextQuantity);
        } else if (nextQuantity > quantity && typeof add === "function") {
            add(p);
        }
    };

    const openDetails = (event) => {
        event.stopPropagation();
        if (onSelect) onSelect(p);
    };

    return (
        <article
            className="card product-card-clickable"
            onClick={() => onSelect && onSelect(p)}
            tabIndex={onSelect ? 0 : undefined}
            role={onSelect ? "button" : undefined}
            onKeyDown={(event) => {
                if (onSelect && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onSelect(p);
                }
            }}
        >
            <div className="pic">
                <img
                    src={p.img ? sizedImage(p.img, 600) : PLACEHOLDER_IMG}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER_IMG;
                    }}
                />
                {/* A second photo shows on hover (clothing: the back or another pose) */}
                {Array.isArray(p.gallery) && p.gallery[0] && <img className="picAlt" src={sizedImage(p.gallery[0], 600)} alt="" loading="lazy" decoding="async" aria-hidden="true" />}
                {badge && <label className={discount > 0 ? "hasDiscount" : ""}>{badge}</label>}
                <button className="heart" aria-label="View product details" onClick={openDetails}>♡</button>
                <ProductShare product={p} />
            </div>
            {Array.isArray(p.comboItems) && p.comboItems.length > 0 && <span className="comboChip">🎁 Combo · {p.comboItems.length} items</span>}
            <h3>{p.name}</h3>
            <p>{shortText(p.desc)}</p>
            {picks && (
                <div className="cardPicks" aria-label="Available sizes and colours">
                    {colorsOf(p).slice(0, 5).map((c) => <i key={c.name} title={c.name} style={{ background: c.hex || "#ddd" }} />)}
                    {colorsOf(p).length > 5 && <small>+{colorsOf(p).length - 5}</small>}
                    {sizesOf(p).length > 0 && <small className="cardSizes">{sizesOf(p).length > 4 ? `${sizesOf(p)[0]}–${sizesOf(p)[sizesOf(p).length - 1]}` : sizesOf(p).join(" · ")}</small>}
                </div>
            )}
            <div className="priceRow">
                <strong>{shown.from && <small>From </small>}{money(shown.price)}</strong>
                {discount > 0 && <del className="priceWas">{money(shown.originalPrice)}</del>}
                {discount > 0 && <span className="discountTag">SAVE {discount}%</span>}
                {config && config.freeShippingAbove > 0 && <span>Free shipping above {money(config.freeShippingAbove)}</span>}
            </div>
            {!inStock ? (
                <Button variant="secondary" className="add isOutOfStock" disabled>Out of stock</Button>
            ) : quantity > 0 ? (
                <div className="productCardQty" role="group" aria-label={`Quantity for ${p.name}`} onClick={(event) => event.stopPropagation()}>
                    <button type="button" aria-label={`Decrease ${p.name} quantity`} onClick={(event) => changeQuantity(event, quantity - 1)} disabled={quantity <= 0}>−</button>
                    <span aria-live="polite">{quantity}</span>
                    <button type="button" aria-label={`Increase ${p.name} quantity`} onClick={(event) => changeQuantity(event, quantity + 1)}>+</button>
                </div>
            ) : (
                <Button
                    variant="success"
                    className="add"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (typeof add === "function") add(p);
                    }}
                >
                    {picks ? (inBag ? `Add another · ${inBag} in bag` : "Add to bag") : "Add to cart"}
                </Button>
            )}
        </article>
    );
}
